import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingAppEditor, {
  SupportedValueTypes,
  getStringValueForDependency,
} from 'benchling-apps/BenchlingAppEditor';
import {
  BenchlingAppType,
  BenchlingAppSubtype,
  isParentDependencyType,
  NestedParentDependencyType,
} from 'benchling-apps/BenchlingAppTypes';
import {pluralize} from 'BenchlingUtil';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import uniqueId from 'util/uniqueId';

import {getBenchlingAppConfigurationQuery} from './__generated/getBenchlingAppConfigurationQuery';
import {SubdependencyArray} from './parent-dependency/BaseSubdependencyList';
import BenchlingAppSubdependencyTable from './parent-dependency/BenchlingAppSubdependencyTable';
import {
  ConfigValidationErrorTypes,
  AllConfigValidationErrorTypes,
  DependencyConfigValidation,
  SubdependencyConfigValidation,
  UpdateArrayElementValidationCallback,
} from './validation/ValidationTypes';

type ConfigArrayElement = NonNullable<
  ExtractByTypename<
    getBenchlingAppConfigurationQuery['benchlingApp'],
    'BenchlingAppNode'
  >['configuration'][number]['elements']
>[number];

type ConfigArrayElementDefinition = NonNullable<
  ExtractByTypename<
    getBenchlingAppConfigurationQuery['benchlingApp'],
    'BenchlingAppNode'
  >['configuration'][number]['elementDefinition']
>[number];

export interface Props {
  name: string;
  type: BenchlingAppType;
  subtype: BenchlingAppSubtype | null;
  description: string | null;
  requiredConfig: boolean;
  value: SupportedValueTypes;
  onChangeValue: (newVal: SupportedValueTypes) => void;
  dependencyValidation: DependencyConfigValidation;
  updateDependencyConfigValidation: (dependencyValidation: DependencyConfigValidation) => void;
  isEditable: boolean;
  scalarEnum?: Array<ScalarEnumOption> | null;

  elementDefinition: Array<ConfigArrayElementDefinition> | null;
  minElements?: number | null;
  maxElements?: number | null;
  elements: Array<ConfigArrayElement> | null;
  onChangeElements: (newElements: Array<ConfigArrayElement>) => void;

  subdependencies: SubdependencyArray | null;
  onChangeSubdependency: (newSubdependencies: SubdependencyArray) => void;

  outputFields: SubdependencyArray | null;
  onChangeOutputFields: (newOutputFields: SubdependencyArray) => void;

  updateArrayElementValidation: UpdateArrayElementValidationCallback;
}

// We get all the errors present on the row (from dependencyValidation object) and prioritize invalid input errors
// first followed by field constraint errors. We prioritize invalid input because it validates the main input
// of the row not the fields associated with the selected input.
function getValidationError(dependencyValidation: DependencyConfigValidation): string | null {
  const validationErrors = [
    ...Object.values(dependencyValidation.subdependencyValidation),
    ...Object.values(dependencyValidation.outputSubdependencyValidation),
  ].reduce(
    (acc, subdependencyValidation) => acc.concat(subdependencyValidation.errors),
    dependencyValidation.errors
  );

  const invalidInputError = validationErrors.find(
    (error) => error.type === ConfigValidationErrorTypes.InvalidInputError
  );
  const fieldConstraintsErrors = validationErrors.filter(
    (error) => error.type === ConfigValidationErrorTypes.FieldConstraintsError
  );

  if (invalidInputError !== undefined) {
    return invalidInputError.message;
  }
  if (fieldConstraintsErrors.length > 0) {
    const numInvalidFields = fieldConstraintsErrors.length;
    return `${numInvalidFields} ${pluralize(
      numInvalidFields,
      'field is',
      'fields are'
    )} no longer valid. Update the invalid ${pluralize(
      numInvalidFields,
      'field',
      'fields'
    )} before submitting a new configuration.`;
  }
  return null;
}

const getNumOfAllInvalidSubdepsByTypes = (
  types: Array<ConfigValidationErrorTypes>,
  dependencyValidation: DependencyConfigValidation
): number =>
  getNumInvalidSubdepsByTypes(types, dependencyValidation.subdependencyValidation) +
  getNumInvalidSubdepsByTypes(types, dependencyValidation.outputSubdependencyValidation);

const getNumInvalidSubdepsByTypes = (
  types: Array<ConfigValidationErrorTypes>,
  subdepValidation: SubdependencyConfigValidation
): number =>
  Object.entries(subdepValidation)
    .map(([_, validationResult]) => {
      const isInvalid = validationResult.errors.find((error) => types.includes(error.type)) != null;
      return isInvalid;
    })
    .reduce((acc, isInvalid) => acc + (isInvalid ? 1 : 0), 0);

export const getNumInvalidSubdeps = (dependencyValidation: DependencyConfigValidation): number =>
  getNumOfAllInvalidSubdepsByTypes(AllConfigValidationErrorTypes, dependencyValidation);

export default function BenchlingAppConfigurationRow({
  name,
  type,
  subtype,
  description,
  requiredConfig,
  value,
  onChangeValue,
  dependencyValidation,
  updateDependencyConfigValidation,
  isEditable,
  scalarEnum,
  elementDefinition,
  minElements,
  maxElements,
  elements,
  onChangeElements,
  subdependencies,
  onChangeSubdependency,
  outputFields,
  onChangeOutputFields,
  updateArrayElementValidation,
}: Props): JSX.Element {
  const validationMessage = getValidationError(dependencyValidation);
  return (
    <div className={css(styles.rowContainer)}>
      <FieldLabel
        dataTestAttrs={{component: 'BenchlingAppConfigurationRow', element: `label-${name}`}}
        htmlFor={uniqueId('BenchlingAppConfigurationRow')}
        isRequired={requiredConfig}
        label={name}
        labelStyle={FieldLabel.STYLES.LABEL}
      />
      {description && <div className={css(styles.description)}>{description}</div>}

      <BenchlingAppEditor
        name={name}
        type={type}
        subtype={subtype}
        value={value}
        requiredConfig={requiredConfig}
        elementDefinition={elementDefinition}
        scalarEnum={scalarEnum ?? null}
        minElements={minElements ?? null}
        maxElements={maxElements ?? null}
        elements={elements}
        onChangeValue={onChangeValue}
        onChangeElements={onChangeElements}
        dependencyValidation={dependencyValidation}
        updateDependencyConfigValidation={updateDependencyConfigValidation}
        updateArrayElementValidation={updateArrayElementValidation}
        isEditable={isEditable}
        schemaId={null}
      />

      {validationMessage != null && (
        <div className={css(styles.errorBannerContainer)}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text={validationMessage} />
        </div>
      )}

      {isParentDependencyType(type) && subdependencies && subdependencies.length > 0 && (
        <BenchlingAppSubdependencyTable
          onChange={onChangeSubdependency}
          dependencyValidation={dependencyValidation}
          updateDependencyConfigValidation={updateDependencyConfigValidation}
          disabled={!isEditable}
          subdependencies={subdependencies}
          parentDependencySelectedId={getStringValueForDependency(value)}
          type={type}
        />
      )}
      {type === BenchlingAppType.workflow_task_schema && outputFields && outputFields.length > 0 && (
        <BenchlingAppSubdependencyTable
          onChange={onChangeOutputFields}
          dependencyValidation={dependencyValidation}
          updateDependencyConfigValidation={updateDependencyConfigValidation}
          disabled={!isEditable}
          subdependencies={outputFields}
          parentDependencySelectedId={getStringValueForDependency(value)}
          type={NestedParentDependencyType.workflow_task_schema_output}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  rowContainer: {
    marginTop: '30px',
  },
  errorBannerContainer: {
    marginTop: '10px',
  },
  description: {
    marginBottom: '8px',
  },
});
